<div class="expense-entry" id="expenseEntryDialog">
    <div class="expense-entry-header">
        <h2>{{ title }}</h2>
        <p-message
            *ngIf="failureMessage"
            severity="error"
            [text]="failureMessage"
        ></p-message>
        <p-message
            *ngIf="successMessage"
            severity="success"
            [text]="successMessage"
        ></p-message>
    </div>

    <div class="expense-entry-body">
        <form [formGroup]="entryForm">
            <p-accordion
                [multiple]="true"
                [(activeIndex)]="activeAccordionIndexes"
            >
                <p-accordionTab header="General">
                    <ng-container *ngTemplateOutlet="generalSection"></ng-container>
                </p-accordionTab>

                <!-- Per Diem: Travel -->
                <p-accordionTab
                    *ngIf="isPerDiem"
                    header="Travel"
                >
                    <div class="expense-entry-section">
                        <div class="expense-entry-row">
                            <p-floatLabel variant="on">
                                <p-datepicker
                                    inputId="entryTravelStart"
                                    formControlName="TravelStartDate"
                                    appendTo="body"
                                    [yearNavigator]="true"
                                    [monthNavigator]="true"
                                    yearRange="2010:2030"
                                    [disabled]="!canEdit"
                                    styleClass="w-full"
                                ></p-datepicker>
                                <label for="entryTravelStart">Travel Start</label>
                            </p-floatLabel>
                            <p-floatLabel variant="on">
                                <p-datepicker
                                    inputId="entryTravelEnd"
                                    formControlName="TravelEndDate"
                                    appendTo="body"
                                    [yearNavigator]="true"
                                    [monthNavigator]="true"
                                    yearRange="2010:2030"
                                    [disabled]="!canEdit"
                                    styleClass="w-full"
                                ></p-datepicker>
                                <label for="entryTravelEnd">Travel End</label>
                            </p-floatLabel>
                        </div>

                        <p-floatLabel variant="on">
                            <p-select
                                inputId="entryLocale"
                                formControlName="Locale"
                                [options]="locales"
                                optionLabel="LocaleDesc"
                                dataKey="LocaleID"
                                appendTo="body"
                                [disabled]="!canEdit"
                                styleClass="w-full"
                            ></p-select>
                            <label for="entryLocale">Locale</label>
                        </p-floatLabel>

                        <p-floatLabel variant="on">
                            <p-select
                                inputId="entryPerDiemType"
                                formControlName="PerDiemTypeID"
                                [options]="filteredPerDiemTypes"
                                optionLabel="PerDiemTypeDesc"
                                optionValue="PerDiemTypeID"
                                appendTo="body"
                                [disabled]="!canEdit"
                                styleClass="w-full"
                            ></p-select>
                            <label for="entryPerDiemType">Type</label>
                        </p-floatLabel>

                        <p-floatLabel variant="on">
                            <p-select
                                inputId="entryTripLeg"
                                formControlName="TripLegType"
                                [options]="tripLegTypes"
                                optionLabel="TripLegTypeDesc"
                                dataKey="TripLegTypeID"
                                appendTo="body"
                                [disabled]="!canEdit"
                                styleClass="w-full"
                            ></p-select>
                            <label for="entryTripLeg">Trip Leg</label>
                        </p-floatLabel>
                    </div>
                </p-accordionTab>

                <!-- Per Diem: Travel Location -->
                <p-accordionTab
                    *ngIf="isPerDiem"
                    header="Travel Location"
                >
                    <div class="expense-entry-section">
                        <div
                            class="location-link"
                            *ngIf="canEdit"
                            (click)="onLocationClick()"
                        >
                            {{ locationDisplay || 'Select location...' }}
                        </div>
                        <div *ngIf="!canEdit">
                            {{ locationDisplay || '—' }}
                        </div>

                        <p-message
                            *ngIf="
                                entryForm.hasError('travelDateOrder') &&
                                (entryForm.touched || entryForm.dirty)
                            "
                            severity="error"
                            [text]="entryForm.getError('travelDateOrder')"
                        ></p-message>

                        <p-floatLabel variant="on" *ngIf="travelRates?.length">
                            <p-select
                                inputId="entryDestination"
                                formControlName="TravelRate"
                                [options]="travelRates"
                                optionLabel="Destination"
                                dataKey="Destination"
                                [filter]="true"
                                filterBy="Destination"
                                appendTo="body"
                                [disabled]="!canEdit"
                                styleClass="w-full"
                            ></p-select>
                            <label for="entryDestination">Destination</label>
                        </p-floatLabel>
                        <p-message
                            *ngIf="
                                entryForm.get('TravelRate')?.invalid &&
                                entryForm.get('TravelRate')?.touched
                            "
                            severity="error"
                            text="Destination is required"
                        ></p-message>
                        <p-message
                            *ngIf="
                                (entryForm.get('Country_Area')?.invalid ||
                                    entryForm.get('Destination_Location')
                                        ?.invalid) &&
                                (entryForm.get('Country_Area')?.touched ||
                                    entryForm.get('Destination_Location')
                                        ?.touched ||
                                    entryForm.touched)
                            "
                            severity="error"
                            text="Country/Area and Location are required"
                        ></p-message>

                        <div
                            class="expense-entry-rates"
                            *ngIf="entryForm.value.TravelRate as rate"
                        >
                            <div *ngIf="showMealsRate">
                                <label>M&amp;IE Rate:</label>{{ rate.Meals_Rate }}
                            </div>
                            <div *ngIf="showLodgingRate">
                                <label>Lodging Rate:</label>{{ rate.Lodging_Rate }}
                            </div>
                            <div>
                                <label>Days:</label>{{ rate.TravelDays }}
                            </div>
                            <div>
                                <label>Amount:</label>{{ rate.Amount }}
                            </div>
                        </div>
                    </div>
                </p-accordionTab>

                <!-- Mileage: Travel (Locale / dates / mode before From-To) -->
                <p-accordionTab
                    *ngIf="isMileage"
                    header="Travel"
                >
                    <ng-container
                        *ngTemplateOutlet="mileageTravelSection"
                    ></ng-container>
                </p-accordionTab>

                <!-- Mileage: Travel From -->
                <p-accordionTab
                    *ngIf="isMileage"
                    header="Travel From"
                >
                    <div class="expense-entry-section">
                        <div
                            class="location-link"
                            *ngIf="canEdit"
                            (click)="onFromLocationClick()"
                        >
                            {{ fromLocationDisplay || 'Select from location...' }}
                        </div>
                        <div *ngIf="!canEdit">
                            {{ fromLocationDisplay || '—' }}
                        </div>
                        <p-message
                            *ngIf="
                                entryForm.hasError('location1') &&
                                (entryForm.touched || entryForm.dirty)
                            "
                            severity="error"
                            [text]="entryForm.getError('location1')"
                        ></p-message>
                    </div>
                </p-accordionTab>

                <!-- Mileage: Travel To -->
                <p-accordionTab
                    *ngIf="isMileage"
                    header="Travel To"
                >
                    <div class="expense-entry-section">
                        <div
                            class="location-link"
                            *ngIf="canEdit"
                            (click)="onToLocationClick()"
                        >
                            {{ toLocationDisplay || 'Select to location...' }}
                        </div>
                        <div *ngIf="!canEdit">
                            {{ toLocationDisplay || '—' }}
                        </div>
                        <p-message
                            *ngIf="
                                entryForm.hasError('location2') &&
                                (entryForm.touched || entryForm.dirty)
                            "
                            severity="error"
                            [text]="entryForm.getError('location2')"
                        ></p-message>
                    </div>
                </p-accordionTab>

                <!-- Receipt fields -->
                <p-accordionTab
                    *ngIf="showReceiptSection"
                    header="Receipt"
                >
                    <ng-container *ngTemplateOutlet="receiptSection"></ng-container>
                </p-accordionTab>

                <!-- Attachments -->
                <p-accordionTab
                    *ngIf="showReceiptSection"
                    header="Receipt Attachments"
                >
                    <ng-container
                        *ngTemplateOutlet="attachmentsSection"
                    ></ng-container>
                </p-accordionTab>
            </p-accordion>

            <ng-template #generalSection>
                <div class="expense-entry-section">
                    <div
                        class="expense-entry-summary-info"
                        *ngIf="summary?.ExpReportNbr > 0"
                    >
                        <div class="expense-entry-summary-line">
                            Report #: {{ reportLineLabel }}
                        </div>
                        <div
                            class="expense-entry-summary-line"
                            *ngIf="summary.ReportTotal != null"
                        >
                            Total:
                            {{ summary.ReportTotal | currency }}
                        </div>
                    </div>

                    <p-floatLabel variant="on">
                        <p-datepicker
                            inputId="entryReportDate"
                            formControlName="ReportDate"
                            appendTo="body"
                            [yearNavigator]="true"
                            [monthNavigator]="true"
                            yearRange="2010:2030"
                            [minDate]="minDate"
                            [maxDate]="maxDate"
                            [disabled]="!canEdit || !reportDateEnabled"
                            styleClass="w-full"
                        ></p-datepicker>
                        <label for="entryReportDate">Report Date</label>
                    </p-floatLabel>

                    <p-floatLabel variant="on">
                        <input
                            pInputText
                            id="entryPurpose"
                            class="w-full"
                            formControlName="Purpose"
                            [readonly]="!canEdit"
                        />
                        <label for="entryPurpose">
                            {{
                                purposeRequired
                                    ? 'Purpose (required)'
                                    : 'Purpose'
                            }}
                        </label>
                    </p-floatLabel>

                    <p-floatLabel variant="on">
                        <p-select
                            inputId="entryTask"
                            formControlName="SelectedTask"
                            [options]="taskOptions"
                            optionLabel="TaskLongName"
                            [filter]="true"
                            filterBy="TaskLongName"
                            appendTo="body"
                            [virtualScroll]="true"
                            [itemSize]="32"
                            [disabled]="!canEdit"
                            styleClass="w-full"
                        ></p-select>
                        <label for="entryTask">Task</label>
                    </p-floatLabel>

                    <p-floatLabel variant="on">
                        <p-select
                            inputId="entryExpenseItem"
                            formControlName="SelectedExpenseItem"
                            [options]="expenseItemOptions"
                            optionLabel="ExpenseItem"
                            dataKey="EIListID"
                            [filter]="true"
                            filterBy="ExpenseItem"
                            appendTo="body"
                            [virtualScroll]="true"
                            [itemSize]="32"
                            [disabled]="!canEdit"
                            styleClass="w-full"
                        ></p-select>
                        <label for="entryExpenseItem">Expense Item</label>
                    </p-floatLabel>
                </div>
            </ng-template>

            <ng-template #mileageTravelSection>
                <div class="expense-entry-section">
                    <div class="expense-entry-row">
                        <p-floatLabel variant="on">
                            <p-datepicker
                                inputId="entryMileageStart"
                                formControlName="TravelStartDate"
                                appendTo="body"
                                [yearNavigator]="true"
                                [monthNavigator]="true"
                                yearRange="2010:2030"
                                [disabled]="!canEdit"
                                styleClass="w-full"
                            ></p-datepicker>
                            <label for="entryMileageStart">Travel Start</label>
                        </p-floatLabel>
                        <p-floatLabel variant="on">
                            <p-datepicker
                                inputId="entryMileageEnd"
                                formControlName="TravelEndDate"
                                appendTo="body"
                                [yearNavigator]="true"
                                [monthNavigator]="true"
                                yearRange="2010:2030"
                                [disabled]="!canEdit"
                                styleClass="w-full"
                            ></p-datepicker>
                            <label for="entryMileageEnd">Travel End</label>
                        </p-floatLabel>
                    </div>

                    <p-floatLabel variant="on">
                        <p-select
                            inputId="entryMileageLocale"
                            formControlName="Locale"
                            [options]="mileageLocales"
                            optionLabel="LocaleDesc"
                            dataKey="LocaleID"
                            appendTo="body"
                            [disabled]="!canEdit"
                            styleClass="w-full"
                        ></p-select>
                        <label for="entryMileageLocale">Locale</label>
                    </p-floatLabel>

                    <p-floatLabel variant="on">
                        <p-select
                            inputId="entryTravelMode"
                            formControlName="TravelMode"
                            [options]="travelModes"
                            optionLabel="RateDesc"
                            dataKey="RateTypeID"
                            appendTo="body"
                            [disabled]="!canEdit"
                            styleClass="w-full"
                        ></p-select>
                        <label for="entryTravelMode">Travel Mode</label>
                    </p-floatLabel>

                    <p-floatLabel variant="on">
                        <input
                            pInputText
                            id="entryMileage"
                            class="w-full"
                            type="number"
                            step="0.1"
                            min="0"
                            formControlName="Mileage"
                            [readonly]="!canEdit"
                        />
                        <label for="entryMileage">Mileage</label>
                    </p-floatLabel>

                    <div class="expense-entry-rates" *ngIf="displayMileageRate as rate">
                        <div>
                            <label>Rate:</label>{{ rate.Mileage_Rate }}
                        </div>
                        <div>
                            <label>Amount:</label>{{ rate.Amount }}
                        </div>
                    </div>
                </div>
            </ng-template>

            <ng-template #receiptSection>
                <div class="expense-entry-section">
                    <p-floatLabel
                        variant="on"
                        [class.receipt-float-error]="
                            receiptFieldError('ReceiptTax')
                        "
                    >
                        <input
                            pInputText
                            id="entryReceiptTax"
                            class="w-full"
                            type="number"
                            step="0.01"
                            min="0"
                            formControlName="ReceiptTax"
                            [readonly]="!canEdit"
                        />
                        <label for="entryReceiptTax">
                            {{ receiptRequired ? 'Tax (required)' : 'Tax' }}
                        </label>
                    </p-floatLabel>
                    <p-floatLabel
                        variant="on"
                        [class.receipt-float-error]="
                            receiptFieldError('ReceiptTotal')
                        "
                    >
                        <input
                            pInputText
                            id="entryReceiptTotal"
                            class="w-full"
                            type="number"
                            step="0.01"
                            min="0"
                            formControlName="ReceiptTotal"
                            [readonly]="!canEdit"
                        />
                        <label for="entryReceiptTotal">
                            {{
                                receiptRequired ? 'Total (required)' : 'Total'
                            }}
                        </label>
                    </p-floatLabel>
                    <p-floatLabel
                        variant="on"
                        [class.receipt-float-error]="
                            receiptFieldError('ReceiptDescription')
                        "
                    >
                        <input
                            pInputText
                            id="entryReceiptDesc"
                            class="w-full"
                            formControlName="ReceiptDescription"
                            [readonly]="!canEdit"
                        />
                        <label for="entryReceiptDesc">
                            {{
                                receiptDescriptionFieldRequired
                                    ? 'Description (required)'
                                    : 'Description'
                            }}
                        </label>
                    </p-floatLabel>
                    <p-floatLabel
                        variant="on"
                        [class.receipt-float-error]="
                            receiptFieldError('PaymentMethod')
                        "
                    >
                        <p-select
                            inputId="entryPaymentMethod"
                            formControlName="PaymentMethod"
                            [options]="userPaymentMethods"
                            optionLabel="Paymentmethod"
                            dataKey="Creditaccountlistid"
                            appendTo="body"
                            [disabled]="!canEdit"
                            styleClass="w-full"
                        ></p-select>
                        <label for="entryPaymentMethod">
                            {{
                                receiptRequired
                                    ? 'Payment Method (required)'
                                    : 'Payment Method'
                            }}
                        </label>
                    </p-floatLabel>
                </div>
            </ng-template>

            <ng-template #attachmentsSection>
                <div class="expense-entry-section">
                    <p-fileUpload
                        *ngIf="canEdit"
                        #attachmentUploader
                        mode="basic"
                        chooseLabel="Add Attachment"
                        chooseIcon="pi pi-plus"
                        [auto]="true"
                        [customUpload]="true"
                        (uploadHandler)="
                            onAttachmentUpload($event, attachmentUploader)
                        "
                        [multiple]="true"
                        accept="image/*,application/pdf"
                    ></p-fileUpload>

                    <div class="attachment-list" *ngIf="attachments.length">
                        <div
                            class="attachment-item"
                            *ngFor="let file of attachments; let i = index"
                        >
                            <a
                                class="attachment-link"
                                [attr.href]="
                                    getAttachmentViewHref(file) || '#'
                                "
                                target="_blank"
                                rel="noopener"
                                (click)="onAttachmentNameClick(file, $event)"
                            >
                                {{ file.Filename || 'Attachment' }}
                            </a>
                            <button
                                *ngIf="canEdit"
                                pButton
                                type="button"
                                icon="pi pi-times"
                                class="p-button-text p-button-danger p-button-sm"
                                (click)="onRemoveAttachment(i)"
                                aria-label="Remove attachment"
                            ></button>
                        </div>
                    </div>
                    <p-message
                        *ngIf="!attachments.length"
                        severity="secondary"
                        text="No attachments"
                    ></p-message>
                </div>
            </ng-template>
        </form>
    </div>

    <div class="expense-entry-actions">
        <button
            *ngIf="canEdit"
            pButton
            type="button"
            label="Save"
            icon="pi pi-check"
            severity="success"
            [disabled]="saveDisabled"
            [loading]="saving"
            (click)="onSave()"
        ></button>
        <button
            pButton
            type="button"
            [label]="canEdit ? 'Cancel' : 'Close'"
            icon="pi pi-times"
            severity="danger"
            [disabled]="saving"
            (click)="onCancel()"
        ></button>
    </div>
</div>

<p-dialog
    [(visible)]="showAddressDialog"
    [modal]="true"
    [closable]="false"
    [closeOnEscape]="false"
    appendTo="body"
    [header]="addressDialogHeader"
    [style]="{ width: '32rem' }"
>
    <ef-expense-detail-city-state-zip-container
        *ngIf="showAddressDialog"
        [selectedCity]="{ City: addressContext.city }"
        [selectedState]="{ State: addressContext.state }"
        [selectedPostalCode]="addressContext.zip"
        [selectedCountryArea]="{ CountryArea: addressContext.countryArea }"
        [selectedLocation]="{ Destination: addressContext.destination }"
        [locale]="addressLocaleId"
        [(showAddressDialog)]="showAddressDialog"
        (SetAddressData)="onSetAddressData($event)"
    >
    </ef-expense-detail-city-state-zip-container>
</p-dialog>

<p-confirmDialog></p-confirmDialog>
